/**
 * i18n core configuration.
 *
 * Single source of truth for which locales the app supports and which one is
 * the fallback. New languages slot in here once their dictionary lands in
 * `./locales/<code>.ts`.
 */

export const SUPPORTED_LOCALES = ["en", "nl"] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

/** Locales we plan to add but haven't shipped a dictionary for yet. The
 *  resolver will accept these as input but downgrade to `DEFAULT_LOCALE`
 *  until their dictionary is added to `./locales/`. */
export const PLANNED_LOCALES = ["de", "fr", "es"] as const;
export type PlannedLocale = (typeof PLANNED_LOCALES)[number];

export const DEFAULT_LOCALE: SupportedLocale = "en";

export const LOCALE_LABEL: Record<SupportedLocale, string> = {
  en: "English",
  nl: "Nederlands",
};

export function isSupportedLocale(value: unknown): value is SupportedLocale {
  return typeof value === "string" && (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

/** Coerce any string into a supported locale, falling back to the default
 *  when the value is missing/unsupported. Use this everywhere we read a
 *  locale from an untrusted source (URL, header, body). */
export function coerceLocale(value: unknown): SupportedLocale {
  if (isSupportedLocale(value)) return value;
  if (typeof value === "string") {
    // Accept BCP-47 tags like "nl-NL" → "nl".
    const base = value.toLowerCase().split(/[-_]/)[0];
    if (isSupportedLocale(base)) return base;
  }
  return DEFAULT_LOCALE;
}
